import { z } from 'zod'

import { PurchaseExcelPreviewRow } from '../../../services/purchases.service'

const optionalNumber = (minimum = 0) => z.preprocess(
  (value) => value === '' || value === undefined || Number.isNaN(value) ? undefined : Number(value),
  z.number().min(minimum).optional(),
)

const catalogSelectionSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  requires_lot: z.boolean().optional(),
})

const emptyCatalogSelections = () => ({
  category: null,
  department: null,
  unit: null,
  productType: null,
})

const existingProductSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  barcode: z.string(),
  currentStock: z.number(),
  category: z.string().nullable(),
  department: z.string().nullable(),
  unit: z.string().nullable(),
  productType: z.string().nullable(),
  requiresLot: z.boolean(),
  satKeyCode: z.string().nullable(),
  satKeyName: z.string().nullable(),
  currentCost: z.string().nullable(),
  currentPrice: z.string().nullable(),
  currentMargin: z.string().nullable(),
}).optional()

const productSchema = z.object({
  origin: z.enum(['manual', 'excel']).default('manual'),
  rowKey: z.string().default('manual'),
  rowNumber: z.number().optional(),
  matchStatus: z.enum(['new_product', 'existing_product', 'blocked']).default('new_product'),
  previewErrors: z.array(z.string()).default([]),
  previewWarnings: z.array(z.string()).default([]),
  catalogSelections: z.object({
    category: catalogSelectionSchema.nullable(),
    department: catalogSelectionSchema.nullable(),
    unit: catalogSelectionSchema.nullable(),
    productType: catalogSelectionSchema.nullable(),
  }).default(emptyCatalogSelections),
  existingProduct: existingProductSchema,
  name: z.string().trim(),
  description: z.string().trim(),
  barcode: z.string().trim(),
  satKeyCode: z.string().trim(),
  satKeyName: z.string().trim(),
  categoryId: z.string(),
  departmentId: z.string(),
  productTypeId: z.string(),
  unitId: z.string(),
  quantity: z.preprocess(
    (value) => value === '' || value === undefined || Number.isNaN(value) ? undefined : Number(value),
    z.number().int('La cantidad debe ser un número entero').min(1, 'La cantidad debe ser mayor a cero').optional(),
  ),
  cost: optionalNumber(),
  margin: optionalNumber(),
  taxId: z.string().optional(),
  requiresLot: z.boolean().default(false),
  lotNumber: z.string().trim().optional(),
  expiryDate: z.string().optional(),
  batchMode: z.enum(['new', 'existing']).optional(),
  productBatchId: z.string().optional(),
}).superRefine((product, context) => {
  if (product.matchStatus === 'blocked') {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['barcode'], message: product.previewErrors[0] || 'Esta fila está bloqueada' })
  }

  if (product.matchStatus === 'new_product') {
    const required: Array<[keyof typeof product, string]> = [
      ['name', 'El nombre es obligatorio'],
      ['description', 'La descripción es obligatoria'],
      ['barcode', 'El código de barras es obligatorio'],
      ['categoryId', 'Categoría requerida'],
      ['departmentId', 'Departamento requerido'],
      ['productTypeId', 'Tipo de producto requerido'],
      ['unitId', 'Unidad requerida'],
    ]
    required.forEach(([field, message]) => {
      if (!String(product[field] ?? '').trim()) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message })
      }
    })

  }

  if (product.matchStatus !== 'blocked') {
    if (product.quantity === undefined) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['quantity'], message: 'La cantidad recibida es obligatoria' })
    }
    if (product.cost === undefined) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['cost'], message: 'El costo sin impuesto es obligatorio' })
    }
    if (!product.taxId) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['taxId'], message: 'Selecciona un impuesto' })
    }
    if (product.margin === undefined) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['margin'], message: 'El porcentaje de utilidad es obligatorio' })
    }
  }
  if (product.lotNumber && !product.expiryDate) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['expiryDate'], message: 'Captura la caducidad del lote' })
  }
  if (product.expiryDate && !product.lotNumber) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['lotNumber'], message: 'Captura el número de lote' })
  }
  if (product.requiresLot && product.quantity !== undefined) {
    if (product.batchMode === 'existing' && !product.productBatchId) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['productBatchId'], message: 'Selecciona el lote existente' })
    }
    if ((product.batchMode ?? 'new') === 'new' && !product.lotNumber) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['lotNumber'], message: 'Captura el número de lote' })
    }
    if ((product.batchMode ?? 'new') === 'new' && !product.expiryDate) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['expiryDate'], message: 'Captura la caducidad del lote' })
    }
  }
})

export const manualEntrySchema = z.object({
  sucursalId: z.string().min(1, 'Selecciona la sucursal destino'),
  noteNumber: z.string().trim().min(1, 'El número de nota es obligatorio'),
  supplierId: z.string().min(1, 'Selecciona un proveedor'),
  purchaseDate: z.string().min(1, 'La fecha de compra es obligatoria'),
  entryDate: z.string().min(1, 'La fecha de ingreso es obligatoria'),
  paymentCondition: z.enum(['cash', 'credit'], { message: 'Selecciona forma de pago' }),
  paymentMethodId: z.string().min(1, 'Selecciona método de pago'),
  products: z.array(productSchema).min(1, 'Agrega al menos un producto para ingresar').max(100, 'Solo se permiten 100 partidas por compra'),
})

export type ManualEntryFormValues = z.infer<typeof manualEntrySchema>

export function purchaseItemBadgeLabel(
  status: ManualEntryFormValues['products'][number]['matchStatus'],
  index: number,
): string {
  const statusLabel = status === 'existing_product' ? 'Existente' : status === 'blocked' ? 'Bloqueado' : 'Nuevo'

  return `${statusLabel} · Partida ${index + 1}`
}

export function excelRowDiagnostic(rowNumber?: number): string | null {
  return rowNumber === undefined ? null : `Fila ${rowNumber} del Excel`
}

export function mapExcelPreviewRow(row: PurchaseExcelPreviewRow): ManualEntryFormValues['products'][number] {
  const existing = row.resolved_product
  const imported = row.imported

  return {
    origin: 'excel',
    rowKey: row.row_key,
    rowNumber: row.row_number,
    matchStatus: row.status,
    previewErrors: row.errors,
    previewWarnings: row.warnings,
    catalogSelections: {
      category: row.resolved_catalogs.category,
      department: row.resolved_catalogs.department,
      unit: row.resolved_catalogs.unit,
      productType: row.resolved_catalogs.product_type,
    },
    existingProduct: existing ? {
      id: existing.id,
      name: existing.name,
      description: existing.description,
      barcode: existing.barcode,
      currentStock: existing.current_stock,
      category: existing.category,
      department: existing.department,
      unit: existing.unit,
      productType: existing.product_type,
      requiresLot: existing.requires_lot,
      satKeyCode: existing.sat_product_key_code,
      satKeyName: existing.sat_product_key_name,
      currentCost: existing.price?.cost_without_taxes ?? null,
      currentPrice: existing.price?.final_price ?? null,
      currentMargin: existing.price?.profit_percentage ?? null,
    } : undefined,
    name: existing?.name ?? imported.name ?? '',
    description: existing?.description ?? imported.description ?? '',
    barcode: imported.barcode ?? '',
    satKeyCode: existing?.sat_product_key_code ?? imported.sat_product_key_code ?? '',
    satKeyName: existing?.sat_product_key_name ?? imported.sat_product_key_name ?? '',
    categoryId: existing ? '' : String(row.resolved_catalogs.category?.id ?? ''),
    departmentId: existing ? '' : String(row.resolved_catalogs.department?.id ?? ''),
    productTypeId: existing ? '' : String(row.resolved_catalogs.product_type?.id ?? ''),
    unitId: existing ? '' : String(row.resolved_catalogs.unit?.id ?? ''),
    quantity: imported.quantity && imported.quantity > 0 ? imported.quantity : undefined,
    cost: imported.cost_without_taxes === null ? undefined : Number(imported.cost_without_taxes),
    margin: imported.profit_percentage === null ? undefined : Number(imported.profit_percentage),
    taxId: row.suggested_tax ? String(row.suggested_tax.id) : '',
    requiresLot: existing?.requires_lot ?? row.resolved_catalogs.product_type?.requires_lot ?? false,
    lotNumber: '',
    expiryDate: '',
    batchMode: 'new',
    productBatchId: '',
  }
}
